import axios, { AxiosRequestConfig } from 'axios';
import { URLSearchParams } from 'url';
import { Executable } from './executable';

const authMapping: {
  [key: string]: string;
} = {
  'api.bambumeta.software': 'https://api-auth.bambumeta.software/oauth2/token',
  'test.api.bambumeta.software':
    'https://core-19726821-1a18-5db3-b494-14e2d206f8b7-bambu.auth.us-east-1.amazoncognito.com/oauth2/token',
  'nft-test': 'https://localhost/oauth2/token',
  'api-pat.bambumeta.software':
    'https://core-b5554626-d173-5e90-8cd2-f906d837daa5-bambu.auth.us-east-1.amazoncognito.com/oauth2/token',
  'fodkt8gsm4.execute-api.us-east-1.amazonaws.com':
    'https://core-b5554626-d173-5e90-8cd2-f906d837daa5-bambu.auth.us-east-1.amazoncognito.com/oauth2/token',
  'sandbox.api.bambumeta.software': 'https://sandbox-auth.bambumeta.software/oauth2/token',
  'temp.api.bambumeta.software':
    'https://core-04e7eb87-a3bf-5376-8855-54456a8555fd-bambu.auth.us-east-1.amazoncognito.com/oauth2/token',
  'api.bambuinnovation.com': 'https://api-auth.bambuinnovation.com/oauth2/token',
  'api.bambu-meta.com': 'https://api-auth.bambu-meta.com/oauth2/token',
  'api.faiola.net': 'https://api-auth.faiola.net/oauth2/token',
};

/**
 * The BambuClient is used to invoke the BambuMeta Client APIs.  This class captures the connection information, which handles the authentication
 * access to the BambuMeta APIs.
 */
export class BambuClient {
  private _clientId: string;

  private _clientSecret: string;

  private _domain: string;

  private _rootPath: string;

  private _token: any;

  private _tenantId: string;

  /**
   * The constructor requires the tenantId, clientId and clientSecret to properly construct requests to BambuMeta.
   * @param tenantId Your tenantId
   * @param clientId Your clientId
   * @param clientSecret Your client secret
   * @param domain The API domain (eg. api.bambumeta.software)
   * @param rootPath The root of the APIs (eg. nft)
   */
  constructor(
    tenantId: string,
    clientId: string,
    clientSecret: string,
    domain: string = 'api.bambumeta.software',
    rootPath: string = 'nft'
  ) {
    this._tenantId = tenantId;
    this._clientId = clientId;
    this._clientSecret = clientSecret;
    this._domain = domain;
    this._rootPath = rootPath;
  }

  /**
   * This internal function is called to retrieve the BambuMeta Access Token when needed
   * @returns The access token.
   */
  private async getBambuToken(): Promise<string> {
    if (this._token) {
      return this._token.access_token;
    }
    const params = new URLSearchParams({ grant_type: 'client_credentials' });
    return axios
      .post(authMapping[this._domain], params.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Basic ${Buffer.from(`${this._clientId}:${this._clientSecret}`).toString(
            'base64'
          )}`,
        },
      })
      .then((response) => {
        this._token = response.data;
        return response.data.access_token;
      })
      .catch((error) => {
        throw error;
      });
  }

  /**
   * This function invokes the BambuMeta API request.
   * @param executeable Take an object of type <Executable<any>> invokes a BambuMeta API call, and returns back the expected response object.
   * @returns The executable repsonse type for the API request.
   */
  public async execute<T extends Executable<any>>(
    executeable: T
  ): Promise<T extends Executable<infer U> ? U : never> {
    const input: AxiosRequestConfig = {
      method: executeable.getMethod(),
      url: `https://${this._domain}/${
        executeable.getRootPath() || this._rootPath
      }/${executeable.getURLPath(this._tenantId)}`,
      headers: {
        Authorization: `Bearer ${await this.getBambuToken()}`,
      },
    };

    const prepareForRequest = {
      execute: async (executable: Executable<any>) => {
        return this.execute(executable);
      },
    };

    await executeable.prepareRequest({
      execute: async (executable: Executable<any>) => {
        return this.execute(executable);
      },
    });

    const _body = await executeable.getBody();
    if (_body) {
      input.data = _body;
    }

    return axios
      .request(input)
      .then((response) => {
        return executeable.getResponse(response.data);
      })
      .catch((error) => {
        if (error?.response?.data?.message?.includes('The incoming token has expired!')) {
          this._token = undefined;
          return this.execute(executeable);
        }

        return Promise.reject(error);
      });
  }
}
